import {
  createElement,
  useLayoutEffect,
  useRef,
  type CSSProperties,
  type FocusEvent,
  type FormEvent,
} from 'react';

export interface MemberSpotlightEditableTextProps {
  /** HTML tag to render. */
  as: 'h3' | 'p' | 'span';
  /** Display text. */
  value: string;
  /** Optional class names. */
  className?: string;
  /** Inline styles matching the surrounding Studio section. */
  style?: CSSProperties;
  /** When true, the field is contentEditable. */
  editable: boolean;
  /** @param value - Updated text while typing. */
  onChange?: (value: string) => void;
  /** @param value - Final text after blur. */
  onBlur?: (value: string) => void;
}

/**
 * Renders Member Spotlight copy. In edit mode the DOM is uncontrolled while
 * focused so React re-renders do not reset the caret position.
 * @param props - Tag, display value, and optional inline edit handlers.
 */
export const MemberSpotlightEditableText = ({
  as,
  value,
  className,
  style,
  editable,
  onChange,
  onBlur,
}: MemberSpotlightEditableTextProps) => {
  const ref = useRef<HTMLElement>(null);
  const isFocusedRef = useRef(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || isFocusedRef.current) {
      return;
    }
    if (element.textContent !== value) {
      element.textContent = value;
    }
  }, [value]);

  if (!editable) {
    return createElement(as, { className, style }, value);
  }

  return createElement(as, {
    ref,
    className: className ? `${className} outline-none` : 'outline-none',
    style,
    contentEditable: true,
    suppressContentEditableWarning: true,
    spellCheck: false,
    onFocus: () => {
      isFocusedRef.current = true;
    },
    onInput: (event: FormEvent<HTMLElement>) => {
      onChange?.(event.currentTarget.textContent ?? '');
    },
    onBlur: (event: FocusEvent<HTMLElement>) => {
      isFocusedRef.current = false;
      onBlur?.(event.currentTarget.textContent ?? '');
    },
  });
};
